import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/base/JsonLd';
import usePageMeta from '@/hooks/usePageMeta';
import { faqs, siteInfo, testimonials } from '@/mocks/news';
import TestimonialSection from '@/pages/home/components/TestimonialSection';
import FaqSection from '@/pages/home/components/FaqSection';

const principles = [
  {
    icon: 'ri-scales-3-line',
    title: '只梳理，不评判',
    desc: '我们只还原话题发生的脉络与讨论焦点，不对任何个人下结论，也不替读者站队。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '不传播未证实信息',
    desc: '涉及存在争议的内容，我们明确标注讨论焦点与信息来源边界，不确定的部分不写。',
  },
  {
    icon: 'ri-timer-flash-line',
    title: '把时间还给读者',
    desc: '每个话题都配要点速览，让你在三分钟内抓住重点，需要深入时再往下读。',
  },
  {
    icon: 'ri-refresh-line',
    title: '错了就改',
    desc: '收到纠错反馈后我们会核对并尽快修订，更新记录对读者保持透明。',
  },
];

const milestones = [
  { year: '2024', title: '从一份清单开始', desc: '最初只是编辑内部用来记录热点的共享文档，后来发现身边人也在用。' },
  { year: '2025', title: '形成分类体系', desc: '把零散话题归纳为五个方向，并加入时间线，补课效率明显提升。' },
  { year: '2026', title: '上线周报与要点速览', desc: '新增订阅周报与要点速览模块，让碎片时间也能跟上讨论节奏。' },
];

export default function About() {
  usePageMeta({
    title: '关于我们 - 看料大瓜｜内容原则与补瓜指南说明',
    description:
      '了解看料大瓜的定位、内容原则与发展理念。我们只做话题梳理与现象观察，不进行人身评价，不传播未经证实的信息，把判断权交回给读者。',
    keywords: '看料大瓜,关于看料大瓜,内容原则,吃瓜补课,热门事件汇总',
    path: '/about',
    ogType: 'website',
  });

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于我们 - 看料大瓜',
      inLanguage: 'zh-CN',
      description: '看料大瓜的品牌介绍、内容原则与发展理念。',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '看料大瓜',
      url: 'https://kanliaodagua.example',
      email: siteInfo.email,
      description: siteInfo.description,
      foundingDate: '2024',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://kanliaodagua.example/' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://kanliaodagua.example/about' },
      ],
    },
  ];

  return (
    <div id="top" className="min-h-screen bg-background-50">
      <JsonLd data={structuredData} />
      <SiteHeader />

      <div className="border-b border-background-200 bg-background-100 pt-24 md:pt-28">
        <div className="mx-auto max-w-7xl px-4 pb-12 md:px-6 md:pb-16">
          <Breadcrumb items={[{ label: '关于我们' }]} />
          <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h1 className="font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
                把散落的热点，整理成一条看得懂的线
              </h1>
              <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">
                看料大瓜是一个专注娱乐热点话题盘点与脉络梳理的内容站点。我们不追求最快，而是希望每一篇都能把事情的来龙去脉讲清楚，让读者花最少的时间，获得最完整的理解。
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-lg bg-secondary-900 p-6 text-secondary-100">
                <h2 className="font-heading text-lg font-bold text-background-50">网站定位</h2>
                <ul className="mt-4 space-y-3 text-sm">
                  <li className="flex items-start gap-2.5">
                    <i className="ri-focus-3-line mt-0.5 text-primary-300" aria-hidden="true"></i>
                    面向想快速了解热点来龙去脉的中文读者
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="ri-focus-3-line mt-0.5 text-primary-300" aria-hidden="true"></i>
                    以时间线与分类为主线组织内容
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="ri-focus-3-line mt-0.5 text-primary-300" aria-hidden="true"></i>
                    只做话题梳理，不参与立场争论
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-4">
            <span className="font-label text-xs tracking-[0.25em] text-accent-600">OUR PRINCIPLES</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">内容原则</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              这四条是我们筛选和撰写内容时的底线，也是你判断本站内容是否值得信任的依据。
            </p>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:col-span-8">
            {principles.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                </div>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-200 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="max-w-2xl">
            <span className="font-label text-xs tracking-[0.25em] text-secondary-600">OUR STORY</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">发展理念</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              从一份内部清单，到一个按时间线组织的内容站，我们始终围绕同一件事：让补课更省力。
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {milestones.map((item) => (
              <div key={item.year} className="rounded-lg border border-background-300 bg-background-50 p-6">
                <span className="font-heading text-2xl font-black text-primary-500">{item.year}</span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TestimonialSection />
      <FaqSection title="常见问题解答" intro="关于内容范围、更新节奏、立场与投稿方式，这里一次说清楚。" />

      <section className="bg-background-200 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 items-center gap-10 rounded-lg bg-background-50 p-6 md:p-10 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-7">
              <span className="font-label text-xs tracking-[0.25em] text-accent-600">CONTACT</span>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">联系我们</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">{siteInfo.contactNote}</p>
              <ul className="mt-6 space-y-3 text-sm text-foreground-700">
                <li className="flex items-center gap-2.5">
                  <i className="ri-mail-line text-primary-500" aria-hidden="true"></i>
                  邮箱：
                  <a
                    href="mailto:hello@kanliaodagua.example"
                    rel="nofollow"
                    className="text-primary-600 underline-offset-4 hover:underline"
                  >
                    {siteInfo.email}
                  </a>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="ri-time-line text-primary-500" aria-hidden="true"></i>
                  回复时间：工作日 48 小时内
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="ri-information-line mt-0.5 text-primary-500" aria-hidden="true"></i>
                  内容纠错请注明话题名称与具体问题点，便于我们快速核对。
                </li>
              </ul>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: 'ri-chat-check-line', label: '话题投稿' },
                  { icon: 'ri-error-warning-line', label: '内容纠错' },
                  { icon: 'ri-briefcase-line', label: '内容合作' },
                  { icon: 'ri-question-answer-line', label: '使用反馈' },
                ].map((item) => (
                  <div key={item.label} className="rounded-lg bg-background-100 p-4 text-center">
                    <i className={`${item.icon} text-2xl text-primary-600`} aria-hidden="true"></i>
                    <p className="mt-2 text-xs text-foreground-700">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}